import React, { type FC } from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import styled from "styled-components";
import { colours, device } from "@helpers";
import { helpsiteTrackLink } from "@helpers/helpsite-mixpanel";
import Icon from "@components/Icon";

/* Counteract Layout.tsx Span negative margin so breadcrumb isn't hidden under sticky nav */
const BREADCRUMB_OFFSET_MOBILE = 30;
const BREADCRUMB_OFFSET_DESKTOP = 60;

const BREADCRUMB_BORDER = "1px solid rgba(0, 0, 0, 0.25)";

const BreadcrumbWrapper = styled.div`
	display: block;
	width: 100%;
	background-color: ${colours.PALE_PEACH};
	box-sizing: border-box;
	margin-top: ${BREADCRUMB_OFFSET_MOBILE}px;
	padding: 16px 15px 18px;
	border-top: ${BREADCRUMB_BORDER};
	border-bottom: ${BREADCRUMB_BORDER};
	overflow-x: hidden;

	@media (min-width: 768px) {
		margin-top: ${BREADCRUMB_OFFSET_DESKTOP}px;
		padding: 20px 24px 24px;
		background-color: #f3f3f3;
		border-top: ${BREADCRUMB_BORDER};
		border-bottom: ${BREADCRUMB_BORDER};
	}

	@media (min-width: ${device.laptop}) {
		margin-top: ${BREADCRUMB_OFFSET_DESKTOP}px;
		margin-left: auto;
		margin-right: auto;
		max-width: 1136px;
		padding: 20px 24px 24px;
		background-color: #f3f3f3;
		border-top: ${BREADCRUMB_BORDER};
		border-bottom: ${BREADCRUMB_BORDER};
	}
`;

const BreadcrumbNav = styled.nav`
	display: flex;
	align-items: center;
	gap: 0;
	width: 100%;
	min-width: 0;
`;

const BreadcrumbSeparator = styled.span`
	display: inline-flex;
	align-items: center;
	flex-shrink: 0;
	margin-left: 8px;
	color: #000000;
`;

/* Single "Help" pill: white rounded rectangle with light border, home icon + bold text (Figma: home menu button) */
const HelpPillLink = styled(Link)`
	display: inline-flex;
	align-items: center;
	gap: 6px;
	padding: 10px 15px 10px 10px;
	background-color: ${colours.WHITE};
	border: 1px solid rgba(0, 0, 0, 0.25);
	border-radius: 12px;
	text-decoration: none;
	color: ${colours.MIDNIGHT_BLUE};
	font-family: var(--euclid-medium);
	font-weight: 700;
	font-size: 14px;
	line-height: 1.27;
	box-sizing: border-box;

	&:hover {
		opacity: 0.9;
		text-decoration: none;
		color: ${colours.MIDNIGHT_BLUE};
	}
`;

/* Breadcrumb trail: parent topic(s) after chevron (Figma: Euclid 14px) */
const BreadcrumbTrail = styled.div`
	display: flex;
	align-items: center;
	flex: 1;
	min-width: 0;
	margin-left: 4px;
	gap: 4px;
`;

const BreadcrumbChevron = styled.span`
	display: inline-flex;
	align-items: center;
	color: #000000;
	flex-shrink: 0;
`;

/* Parent topic text when not a link (same styling, with truncation) */
const BreadcrumbTopicText = styled.span`
	font-family: var(--euclid-regular);
	font-size: 14px;
	font-weight: 400;
	line-height: 1.27;
	color: ${colours.MIDNIGHT_BLUE};
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

/* Parent topic link in breadcrumb trail (Figma: Euclid 14px regular, midnight blue) */
const BreadcrumbTopicLink = styled(Link)`
	font-family: var(--euclid-regular);
	font-size: 14px;
	font-weight: 400;
	line-height: 1.27;
	color: ${colours.MIDNIGHT_BLUE};
	text-decoration: none;
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;

	&:hover {
		text-decoration: underline;
		color: ${colours.MIDNIGHT_BLUE};
	}
`;

export interface BreadcrumbItem {
	label: string;
	href?: string;
}

interface HelpBreadcrumbProps {
	items: BreadcrumbItem[];
}

const HelpBreadcrumb: FC<HelpBreadcrumbProps> = ({ items }) => {
	const router = useRouter();
	const pagePath = router.asPath.split("?")[0];

	if (!items?.length) return null;

	/* First item: "Help" pill with home icon (Figma: home menu button) */
	const helpItem = items[0];
	if (!helpItem) return null;

	/* Additional items: chevron + link (Figma: breadcrumbs - parent topic) */
	const trailItems = items.slice(1);

	return (
		<BreadcrumbWrapper>
			<BreadcrumbNav aria-label="Breadcrumb">
				{helpItem.href ? (
					<HelpPillLink
						href={helpItem.href}
						onClick={() =>
							helpsiteTrackLink({
								helpsite_section: "breadcrumb",
								page_path: pagePath,
								link_href: helpItem.href,
								label: helpItem.label,
							})
						}
					>
						<Icon
							iconName="home"
							iconSizes={{
								iconMobile: 16,
								iconTablet: 16,
								iconLaptop: 16,
								iconDesktop: 16,
								iconDesktopLarge: 16,
							}}
							alt=""
						/>
						{helpItem.label}
					</HelpPillLink>
				) : null}
				{trailItems.length > 0 && (
					<>
						<BreadcrumbSeparator>
							<Icon
								iconName="chevron-right"
								iconSizes={{
									iconMobile: 20,
									iconTablet: 20,
									iconLaptop: 20,
									iconDesktop: 20,
									iconDesktopLarge: 20,
								}}
								alt=""
							/>
						</BreadcrumbSeparator>
						<BreadcrumbTrail>
							{trailItems.map((item, i) => (
								<React.Fragment key={`${item.label}-${i}`}>
									{i > 0 && (
										<BreadcrumbChevron>
											<Icon
												iconName="chevron-right"
												iconSizes={{
													iconMobile: 20,
													iconTablet: 20,
													iconLaptop: 20,
													iconDesktop: 20,
													iconDesktopLarge: 20,
												}}
												alt=""
											/>
										</BreadcrumbChevron>
									)}
									{item.href ? (
										<BreadcrumbTopicLink
											href={item.href}
											onClick={() =>
												helpsiteTrackLink({
													helpsite_section: "breadcrumb",
													page_path: pagePath,
													link_href: item.href,
													label: item.label,
												})
											}
										>
											{item.label}
										</BreadcrumbTopicLink>
									) : (
										<BreadcrumbTopicText>{item.label}</BreadcrumbTopicText>
									)}
								</React.Fragment>
							))}
						</BreadcrumbTrail>
					</>
				)}
			</BreadcrumbNav>
		</BreadcrumbWrapper>
	);
};

export default HelpBreadcrumb;
